/**
 * Frontend datetime helpers — counterpart to the backend
 * `services/datetime_normalizer.py` sentinel.
 *
 * Backend ALWAYS emits ISO-8601 UTC strings (with `+00:00` or trailing `Z`).
 * The browser knows the user's local timezone via `Intl`, so all we have to
 * do is pass the canonical string to a `Date` constructor and let
 * `Intl.DateTimeFormat` render it for the user's locale.
 *
 * Why not `dayjs` / `date-fns`? Both are great, but for the small surface
 * we need (parse + locale-aware format) the platform `Intl` API is enough,
 * has zero bundle cost, and matches the user's OS settings perfectly.
 *
 * Conventions
 * -----------
 * - All input parameters accept `string | number | Date | null | undefined`.
 * - All format helpers return `''` (empty string) when the input is null /
 *   undefined / unparseable, so they're safe to drop into JSX.
 * - Use `formatDateTime(...)` for the default "user local timezone, locale
 *   medium date + time" rendering. Override via the `options` arg if needed.
 */

const DEFAULT_LOCALE_FALLBACK = 'en-GB';

/**
 * Coerce any backend-emitted shape into a `Date` object.
 * Returns `null` on bad / missing input — callers should guard.
 */
export function toDate(value) {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    // Treat anything > 1e12 as ms (post-2001 in milliseconds), otherwise
    // seconds. Matches the backend's coercion logic.
    const ms = value > 1e12 ? value : value * 1000;
    const dt = new Date(ms);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const dt = new Date(trimmed);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  return null;
}

/** Browser-detected IANA timezone (e.g. `"Europe/Istanbul"`). */
export function getUserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** Default browser locale (e.g. `"tr-TR"`, `"en-GB"`). */
export function getUserLocale() {
  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language;
  }
  return DEFAULT_LOCALE_FALLBACK;
}

/**
 * Render a datetime in the user's locale + timezone.
 *
 * Defaults: medium date + short time (e.g. "26 Apr 2026, 12:30").
 * Pass any subset of `Intl.DateTimeFormatOptions` to override.
 */
export function formatDateTime(value, options = {}, locale) {
  const dt = toDate(value);
  if (!dt) return '';
  const opts = {
    year:  'numeric',
    month: 'short',
    day:   '2-digit',
    hour:   '2-digit',
    minute: '2-digit',
    timeZone: getUserTimezone(),
    ...options,
  };
  try {
    return new Intl.DateTimeFormat(locale || getUserLocale(), opts).format(dt);
  } catch {
    return dt.toISOString();
  }
}

/** Shortcut: date only (no time). */
export function formatDate(value, locale) {
  return formatDateTime(value, { hour: undefined, minute: undefined }, locale);
}

/** Shortcut: time only (no date). */
export function formatTime(value, locale) {
  return formatDateTime(
    value,
    { year: undefined, month: undefined, day: undefined },
    locale,
  );
}

/**
 * Human-friendly relative-time label ("3 hours ago", "in 2 days").
 *
 * Uses `Intl.RelativeTimeFormat`. Returns `''` for unparseable input.
 */
export function formatRelative(value, locale) {
  const dt = toDate(value);
  if (!dt) return '';
  const diffMs = dt.getTime() - Date.now();
  const diffSec = Math.round(diffMs / 1000);

  const buckets = [
    [60,                   'second'],
    [60 * 60,              'minute'],
    [60 * 60 * 24,         'hour'],
    [60 * 60 * 24 * 30,    'day'],
    [60 * 60 * 24 * 365,   'month'],
    [Number.POSITIVE_INFINITY, 'year'],
  ];

  let unit = 'second';
  let valueIn = diffSec;
  let prevLimit = 1;
  for (const [limit, candidateUnit] of buckets) {
    if (Math.abs(diffSec) < limit) {
      unit    = candidateUnit;
      valueIn = Math.round(diffSec / prevLimit);
      break;
    }
    prevLimit = limit;
  }
  try {
    return new Intl.RelativeTimeFormat(locale || getUserLocale(), { numeric: 'auto' })
      .format(valueIn, unit);
  } catch {
    return '';
  }
}
